interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/**
 * Decode a JWT payload without verification (client-side only).
 * Server-side verification still happens on every request.
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired, with an optional buffer in seconds.
 * Returns true if token can't be decoded.
 */
export function isTokenExpired(token: string, bufferSeconds = 0): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return Date.now() >= (payload.exp - bufferSeconds) * 1000;
}

/**
 * Get milliseconds until token expiration.
 * Returns null if token can't be decoded, negative if already expired.
 */
export function getTokenExpiresInMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return null;
  return payload.exp * 1000 - Date.now();
}
